import React, { useEffect } from "react";
import {
  Controller,
  get,
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";
import { Input, Label, Tooltip } from "@src/common/components";
import CourieButton from "@src/common/components/Button/Button";
import {
  ExclamationTriangleIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCourierLocale } from "@src/common/courierLocale";
import { PackageTypeCategory } from "@api/graphql/generated/generated-types";
import {
  DEFAULT_FREE_SKID_AMOUNT,
  calculateSkidSpaces,
} from "@src/common/utils/skidSpaceCalculator";
import {
  isPackageItemWeightLocked,
  newPackageWithDefaults,
  packageItemsChanged,
  PackageTypeDefaultDimensions,
} from "@src/common/utils/packageTypeDefaults";
import { dimWeightForPiece } from "@src/common/utils/dimWeight";
import {
  assumesDefaultQuantity,
  concretePackageItemQuantity,
  derivedNumPackages,
  derivedShipmentPackageTypeId,
  totalPackageItemWeight,
} from "@src/common/utils/packageItems";
import { validatePackageItemQuantity } from "@src/common/utils/packageItemCountValidation";
import { useAuth } from "@src/common/hooks/useAuth";
import { PackageItemTypeOrSize } from "./PackageItemTypeOrSize";
import { RequiredItemLabel } from "./RequiredItemLabel";
import { isPackageDimensionsFilled } from "./isPackageDimensionsFilled";
import { packageTypeOrSizeMode } from "./packageTypeOrSizeMode";

export interface PackageItemFormData {
  width?: number | null;
  height?: number | null;
  length?: number | null;
  weight?: number | null;
  /** Per-item type. Null / None is None on create order (no shipment-type inherit). */
  packageTypeId?: string | null;
  /**
   * How many identical pieces this row stands for. Dimensions and `weight` are per piece, so
   * `quantity: 5, weight: 10` is 5 pieces weighing 50 lbs in total. Null reads as one piece.
   */
  quantity?: number | null;
}

export interface PackageTypeOption extends PackageTypeDefaultDimensions {
  id: string;
  name: string;
  category: PackageTypeCategory;
}

interface PackageDimensionsFormProps {
  fieldName: string;
  onModified?: () => void;
  onWeightChange?: (totalWeight: number | null) => void;
  className?: string;
  defaultDimensions?: PackageTypeDefaultDimensions | null;
  /** When empty/omitted, Type UI is hidden. */
  packageTypes?: PackageTypeOption[];
  onPackageItemRemoved?: (index: number) => void;
  /**
   * Surfaces where the first row is the shipment itself rather than an extra package.
   * Untitled when it is the only row; titled Package 1 once a second package exists.
   * It cannot be removed, so the list is never empty.
   */
  hasBasePackage?: boolean;
  arePackageTypesResolved?: boolean;
  packageTypesError?: string | null;
  packageSizeFieldName?: string;
  shipmentPackageTypeIdFieldName?: string;
  /** Create-order: write derived `numPackages` from the rows while typing. */
  numPackagesFieldName?: string;
  isTypeRequired?: boolean;
  isSizeRequired?: boolean;
  isQuantityRequired?: boolean;
  isWeightRequired?: boolean;
  isDimensionsRequired?: boolean;
}

interface PackageDimensionsRowProps {
  fieldName: string;
  index: number;
  control: ReturnType<typeof useFormContext>["control"];
  weightLabel: string;
  /** Courier's dim factor; `null` / `undefined` means dim weight is disabled. */
  dimFactor: number | null | undefined;
  formatWeight: (pounds: number) => string;
  onModified?: () => void;
  onRemove: (index: number) => void;
  onWeightFieldChange: () => void;
  packageTypes?: PackageTypeOption[];
  isBasePackage?: boolean;
  /** Whether this row is the whole list, which only the base row can be. */
  isOnlyPackage?: boolean;
  typeOrSizeMode: ReturnType<typeof packageTypeOrSizeMode>;
  packageSizeFieldName?: string;
  packageTypesError?: string | null;
  isTypeRequired?: boolean;
  isSizeRequired?: boolean;
  isQuantityRequired?: boolean;
  isWeightRequired?: boolean;
  isDimensionsRequired?: boolean;
}

const DIMENSION_FIELDS = [
  { name: "length", placeholder: "Length" },
  { name: "width", placeholder: "Width" },
  { name: "height", placeholder: "Height" },
] as const;

function PackageDimensionsRow({
  fieldName,
  index,
  control,
  weightLabel,
  dimFactor,
  formatWeight,
  onModified,
  onRemove,
  onWeightFieldChange,
  packageTypes,
  isBasePackage,
  isOnlyPackage,
  typeOrSizeMode,
  packageSizeFieldName,
  packageTypesError,
  isTypeRequired,
  isSizeRequired,
  isQuantityRequired,
  isWeightRequired,
  isDimensionsRequired,
}: PackageDimensionsRowProps) {
  // `fieldName` is a runtime string, so react-hook-form cannot infer these paths and
  // hands back `any`. Type each one here, at the watch, so nothing downstream is `any`.
  const length = useWatch({
    control,
    name: `${fieldName}.${index}.length`,
  }) as number | null | undefined;
  const width = useWatch({
    control,
    name: `${fieldName}.${index}.width`,
  }) as number | null | undefined;
  const height = useWatch({
    control,
    name: `${fieldName}.${index}.height`,
  }) as number | null | undefined;
  // The any-of rule lives on the length field (see the Controller below); read its error
  // here so the one message can sit under the whole group instead of under a single box.
  const lengthFieldName: string = `${fieldName}.${index}.length`;
  const { errors } = useFormState({ control, name: lengthFieldName });
  const dimensionsError: string | undefined = get(errors, lengthFieldName)?.message;
  const { getValues: getFormValues } = useFormContext();
  // Width and height only re-run the length rule, so the error clears as soon as any box fills.
  const dimensionRules = (name: (typeof DIMENSION_FIELDS)[number]["name"]) => {
    if (!isDimensionsRequired) return undefined;
    if (name !== "length") return { deps: [lengthFieldName] };
    return {
      validate: () =>
        isPackageDimensionsFilled(getFormValues(`${fieldName}.${index}`) ?? {}) ||
        "Enter at least one dimension",
    };
  };
  const quantity = useWatch({
    control,
    name: `${fieldName}.${index}.quantity`,
  }) as number | null | undefined;
  const weight = useWatch({
    control,
    name: `${fieldName}.${index}.weight`,
  }) as number | null | undefined;
  const packageTypeId = useWatch({
    control,
    name: `${fieldName}.${index}.packageTypeId`,
  }) as string | null | undefined;
  const isWeightLocked: boolean = isPackageItemWeightLocked({
    packageTypeId,
    packageTypes,
  });
  const pieceCount: number = concretePackageItemQuantity({ quantity });
  // Only `weight` is stored, per piece. This box is a view of it, so a piece weight the
  // operator never rounded (a typed total divided by an awkward quantity) still reads back
  // as the total they typed.
  const rowTotalWeight: number | null =
    weight == null ? null : (weight * pieceCount);
  const { setValue } = useFormContext();
  const setRowWeight = (value: number | null) =>
    setValue(`${fieldName}.${index}.weight`, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  // Skid UI only applies to rows whose own type is a skid type; a row with no type has none.
  const isSkidRow: boolean =
    packageTypes?.find((packageType) => packageType.id === packageTypeId)
      ?.category === PackageTypeCategory.Skid;
  const skidSpacesPerPiece: number | null = isSkidRow
    ? calculateSkidSpaces(length, width)
    : null;
  const skidSpaces: number | null =
    skidSpacesPerPiece == null ? null : skidSpacesPerPiece * pieceCount;
  const dimWeightPerPiece: number | null = dimWeightForPiece(
    { length, width, height },
    dimFactor,
  );
  const dimWeight: number | null =
    dimWeightPerPiece == null ? null : dimWeightPerPiece * pieceCount;
  // Oversized is a property of one piece, so it reads the per-piece footprint — five
  // ordinary boxes on one row are five skid spaces but not an oversized package.
  const isOversizedHighlighted =
    skidSpacesPerPiece != null &&
    skidSpacesPerPiece > DEFAULT_FREE_SKID_AMOUNT;

  const quantityPlaceholder: string = assumesDefaultQuantity(
    { length, width, height, quantity },
    !!isBasePackage && !!isOnlyPackage,
  )
    ? "1"
    : "";

  const containerClass = `border rounded-lg p-3 ${
    isOversizedHighlighted
      ? "bg-amber-50 dark:bg-amber-900/30 border-amber-400"
      : "bg-gray-50 dark:bg-gray-800"
  }`;

  return (
    <div data-testid={`package-dimensions-row-${index}`} className={containerClass}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {(!isBasePackage || !isOnlyPackage) && (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Package {index + 1}
            </span>
          )}
          {dimWeight != null && dimWeight > 0 && (
            <span
              data-testid={`package-dimensions-dim-weight-${index}`}
              className="text-xs text-gray-600 dark:text-gray-300"
            >
              Dim weight: {formatWeight(dimWeight)}
            </span>
          )}
          {isOversizedHighlighted && (
            <Tooltip content="This package is oversized — it takes more than one skid space.">
              <ExclamationTriangleIcon
                data-testid={`package-dimensions-warning-icon-${index}`}
                className="h-4 w-4 text-amber-600"
                aria-label="Oversized package warning"
              />
            </Tooltip>
          )}
          {skidSpaces != null && (
            <span
              data-testid={`package-dimensions-skid-count-${index}`}
              className="text-xs text-gray-600 dark:text-gray-300"
            >
              Estimated skid spaces: {skidSpaces}
            </span>
          )}
        </div>
        {!isBasePackage && (
          <CourieButton
            color="gray"
            size="xs"
            aria-label={`Remove package ${index + 1}`}
            onClick={() => onRemove(index)}
          >
            <XMarkIcon className="h-4 w-4" />
          </CourieButton>
        )}
      </div>
      <div
        data-testid={`package-dimensions-fields-${index}`}
        className="flex flex-col gap-3"
      >
        {/* Line one: what the package is and what it weighs. Quantity and Type/Size are
            capped, so the two weight boxes absorb whatever width is left over. */}
        <div className="flex flex-wrap items-start gap-3">
          <PackageItemTypeOrSize
            mode={typeOrSizeMode}
            itemPackageTypeIdFieldName={`${fieldName}.${index}.packageTypeId`}
            packageSizeFieldName={packageSizeFieldName}
            packageTypes={packageTypes}
            packageTypesError={packageTypesError}
            isTypeRequired={isTypeRequired}
            isSizeRequired={isSizeRequired}
            showSize={index === 0}
            onModified={onModified}
          />
          <div className="flex flex-col shrink-0 max-w-[100px]">
            <RequiredItemLabel required={isQuantityRequired}>
              Quantity
            </RequiredItemLabel>
            <Controller
              control={control}
              name={`${fieldName}.${index}.quantity`}
              rules={{
                required: isQuantityRequired ? "Quantity is required" : undefined,
                validate: (value) => validatePackageItemQuantity(value) ?? true,
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Input
                    size="mini"
                    type="number"
                    aria-label={`Quantity for package ${index + 1}`}
                    placeholder={quantityPlaceholder}
                    error={!!error}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      // Number() rather than parseInt() so a fraction or scientific notation
                      // reaches validation intact — parseInt("2e2") is 2, silently discarding
                      // two orders of magnitude from a value the user typed.
                      const raw = e.currentTarget.value;
                      const value = Number(raw);
                      field.onChange(raw === "" || Number.isNaN(value) ? null : value);
                      onModified?.();
                      onWeightFieldChange();
                    }}
                    onBlur={field.onBlur}
                    min={1}
                    step={1}
                  />
                  {error?.message && (
                    <span className="text-xs text-red-500 mt-0.5">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
          <div className="flex flex-col flex-1 min-w-[8rem]">
            <RequiredItemLabel required={isWeightRequired}>
              Weight ({weightLabel} / pc)
            </RequiredItemLabel>
            <Controller
              control={control}
              name={`${fieldName}.${index}.weight`}
              rules={
                isWeightRequired ? { required: "Weight is required" } : undefined
              }
              render={({ field, fieldState: { error } }) => (
                <>
                  <Input
                    size="mini"
                    type="number"
                    aria-label={`Weight per piece for package ${index + 1}`}
                    placeholder="Weight"
                    error={!!error}
                    disabled={isWeightLocked}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const value = parseFloat(e.currentTarget.value);
                      field.onChange(Number.isNaN(value) ? null : value);
                      onModified?.();
                      onWeightFieldChange();
                    }}
                    onBlur={field.onBlur}
                    min={0}
                    step="any"
                  />
                  {error?.message && (
                    <span className="text-xs text-red-500 mt-0.5">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
          <div className="flex flex-col flex-1 min-w-[8rem]">
            <Label>Total Weight ({weightLabel})</Label>
            <Input
              size="mini"
              type="number"
              aria-label={`Total weight for package ${index + 1}`}
              placeholder="Total"
              disabled={isWeightLocked}
              value={rowTotalWeight ?? ""}
              onChange={(e) => {
                const value = parseFloat(e.currentTarget.value);
                // Emptying the box means "no weight" at any quantity, so that still
                // writes. A number, though, says nothing about one piece when there
                // are no pieces to divide into, and Quantity's own validator is
                // already reporting the zero. Writing Infinity would outlive that
                // error: the piece box renders a non-finite number as blank, so the
                // row would look empty while poisoning the shipment total.
                if (!Number.isNaN(value) && pieceCount <= 0) return;
                setRowWeight(
                  Number.isNaN(value) ? null : value / pieceCount,
                );
                onModified?.();
                onWeightFieldChange();
              }}
              min={0}
              step="any"
            />
          </div>
        </div>
        {/* Line two: the dimensions always get their own line, and the three boxes share
            it evenly rather than sitting capped and cramped beside the weight boxes. */}
        <div
          data-testid={`package-dimensions-column-${index}`}
          className="flex flex-col"
        >
          <RequiredItemLabel required={isDimensionsRequired}>
            Dimensions{" "}
            {!isDimensionsRequired && (
              <span className="text-slate-400 text-xs">(Optional)</span>
            )}
          </RequiredItemLabel>
          <div className="flex items-start gap-2">
            {DIMENSION_FIELDS.map(({ name, placeholder }, dimensionIndex) => (
              <React.Fragment key={name}>
                {dimensionIndex > 0 && (
                  // Nudged down to sit on the input line, which stays put when an
                  // error message pushes the column below it taller.
                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    x
                  </span>
                )}
                <div
                  data-testid={`package-dimension-${name}-${index}`}
                  className="flex flex-col flex-1 min-w-0"
                >
                  <Controller
                    control={control}
                    name={`${fieldName}.${index}.${name}`}
                    rules={dimensionRules(name)}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          size="mini"
                          type="number"
                          placeholder={placeholder}
                          error={!!error || !!dimensionsError}
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = parseFloat(e.currentTarget.value);
                            field.onChange(Number.isNaN(value) ? null : value);
                            onModified?.();
                          }}
                          onBlur={field.onBlur}
                          min={0}
                          step="any"
                        />
                        {error?.message && error.message !== dimensionsError && (
                          <span className="text-xs text-red-500 mt-0.5">
                            {error.message}
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
          {dimensionsError && (
            <span className="text-xs text-red-500 mt-0.5">{dimensionsError}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function PackageDimensionsForm({
  fieldName,
  onModified,
  onWeightChange,
  className = "col-span-12",
  defaultDimensions,
  packageTypes,
  onPackageItemRemoved,
  hasBasePackage = false,
  arePackageTypesResolved = true,
  packageTypesError,
  packageSizeFieldName,
  shipmentPackageTypeIdFieldName,
  numPackagesFieldName,
  isTypeRequired,
  isSizeRequired,
  isQuantityRequired,
  isWeightRequired,
  isDimensionsRequired,
}: PackageDimensionsFormProps) {
  const { control, getValues, setValue } = useFormContext();
  const { weightLabel, formatWeight } = useCourierLocale();
  const { courier } = useAuth();
  const dimFactor: number | null | undefined = courier?.courier?.dimFactor;
  const typeOrSizeMode = packageTypeOrSizeMode({
    hasBasePackage,
    packageTypes,
    arePackageTypesResolved,
    packageTypesError,
  });

  useEffect(() => {
    if (!hasBasePackage || typeOrSizeMode === "none") return;

    if (typeOrSizeMode === "size") {
      const items =
        (getValues(fieldName) as PackageItemFormData[] | undefined) ?? [];
      const next: PackageItemFormData[] = items.map((item) =>
        item.packageTypeId == null ? item : { ...item, packageTypeId: null },
      );
      if (packageItemsChanged(items, next)) {
        setValue(fieldName, next);
      }
      if (shipmentPackageTypeIdFieldName) {
        const shipmentType = getValues(shipmentPackageTypeIdFieldName);
        if (shipmentType != null && shipmentType !== "") {
          setValue(shipmentPackageTypeIdFieldName, null);
        }
      }
      return;
    }

    if (packageSizeFieldName) {
      const size = getValues(packageSizeFieldName);
      if (size != null && size !== "") {
        setValue(packageSizeFieldName, null);
      }
    }
  }, [
    fieldName,
    getValues,
    hasBasePackage,
    packageSizeFieldName,
    setValue,
    shipmentPackageTypeIdFieldName,
    typeOrSizeMode,
  ]);

  // Row weight is per piece, so the shipment total bills each row `quantity` times:
  // a row of quantity 5 at 10 lbs contributes 50 lbs.
  const calculateTotalWeight = () => {
    onWeightChange?.(
      totalPackageItemWeight(
        getValues(fieldName) as PackageItemFormData[] | undefined,
      ),
    );
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
  });
  const watchedItems = useWatch({
    control,
    name: fieldName,
  }) as PackageItemFormData[] | undefined;
  const totalQuantity: number | null = derivedNumPackages(
    watchedItems,
    hasBasePackage,
  );
  const totalWeight: number | null = totalPackageItemWeight(watchedItems);

  // Report the shipment total only when it actually changes. The parent writes every
  // report into a watched form field, and that write wakes the item watchers that feed
  // the effect below, so reporting an unchanged total on every items change re-arms
  // itself in an endless render loop that freezes the order form.
  useEffect(() => {
    onWeightChange?.(totalWeight);
  }, [totalWeight]);

  useEffect(() => {
    if (!hasBasePackage) return;

    if (numPackagesFieldName) {
      const nextCount: number | null = derivedNumPackages(
        watchedItems,
        hasBasePackage,
      );
      if (getValues(numPackagesFieldName) !== nextCount) {
        setValue(numPackagesFieldName, nextCount);
      }
    }

    if (shipmentPackageTypeIdFieldName) {
      const nextType: string | null = derivedShipmentPackageTypeId(
        watchedItems,
        typeOrSizeMode,
      );
      if (getValues(shipmentPackageTypeIdFieldName) !== nextType) {
        setValue(shipmentPackageTypeIdFieldName, nextType);
      }
    }
  }, [
    getValues,
    hasBasePackage,
    numPackagesFieldName,
    setValue,
    shipmentPackageTypeIdFieldName,
    typeOrSizeMode,
    watchedItems,
  ]);


  const handleAddPackage = () => {
    append(newPackageWithDefaults(defaultDimensions));
    onModified?.();
  };

  const handleRemovePackage = (index: number) => {
    onPackageItemRemoved?.(index);
    remove(index);
    onModified?.();
    // Use setTimeout to allow react-hook-form to update before recalculating
    setTimeout(calculateTotalWeight, 0);
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Label>Package Information</Label>
          {hasBasePackage && (watchedItems?.length ?? 0) > 0 && (
            <>
              {totalQuantity != null && (
                <span
                  data-testid="package-information-total-quantity"
                  className="text-xs text-gray-600 dark:text-gray-300"
                >
                  Total Quantity: {totalQuantity}
                </span>
              )}
              {totalWeight != null && (
                <span
                  data-testid="package-information-total-weight"
                  className="text-xs text-gray-600 dark:text-gray-300"
                >
                  Total Weight: {formatWeight(totalWeight)}
                </span>
              )}
            </>
          )}
        </div>
        <CourieButton color="gray" size="xs" onClick={handleAddPackage}>
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Package
        </CourieButton>
      </div>

      {fields.length > 0 && (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <PackageDimensionsRow
              key={field.id}
              fieldName={fieldName}
              index={index}
              control={control}
              weightLabel={weightLabel}
              dimFactor={dimFactor}
              formatWeight={formatWeight}
              onModified={onModified}
              onRemove={handleRemovePackage}
              onWeightFieldChange={calculateTotalWeight}
              packageTypes={packageTypes}
              isBasePackage={hasBasePackage && index === 0}
              isOnlyPackage={fields.length === 1}
              typeOrSizeMode={typeOrSizeMode}
              packageSizeFieldName={packageSizeFieldName}
              packageTypesError={packageTypesError}
              isTypeRequired={isTypeRequired}
              isSizeRequired={isSizeRequired}
              isQuantityRequired={isQuantityRequired}
              isWeightRequired={isWeightRequired}
              isDimensionsRequired={isDimensionsRequired}
            />
          ))}
        </div>
      )}

      {fields.length === 0 && (
        <div className="text-sm text-gray-500 italic">
          No package dimensions added. Click &quot;Add Package&quot; to add
          dimensions.
        </div>
      )}
    </div>
  );
}
